import type { DeliveryMetrics, POLineDashboardData, POLineDrilldownMode } from '../../../lib/api/po-line-dashboard';
import type { DrilldownTabContext, POLineDrilldownFilters } from './poLineDrilldownModes';

/** Props shared by the Current Month / Current Week / Per Week / YTD panels. */
export interface POLinePeriodProps {
  data: POLineDashboardData;
  /** Formatter already bound to the payload's reporting currency. */
  formatCurrency: (n: number) => string;
  /** Dashboard filter scope threaded from the shell so drill-downs match the tiles. */
  filters: POLineDrilldownFilters;
  /** Re-fetch the dashboard payload (after a row-level OTIF decision in a drill-down). */
  onRefresh?: () => void;
}

/** Tile figures per drill-down mode — shown in the modal's sibling tab labels. */
export type POLineTabCounts = Partial<Record<POLineDrilldownMode, number>>;

/** Open-modal state for a period panel. */
export interface POLineDrilldownState {
  mode: POLineDrilldownMode;
  periodStart?: string;
  periodEnd?: string;
  /** Grain of the tile that opened the drill: a week carries no arrears stock (W3-T7). */
  periodGrain?: 'week' | 'month';
  periodLabel?: string;
  tabCounts?: POLineTabCounts;
  tabContext?: DrilldownTabContext;
}

/** Map a period's delivery block to the tab counts of the OTIF / OTD groups —
 *  the same numbers the ring and the tile rendered. */
export function deliveryTabCounts(d: DeliveryMetrics): POLineTabCounts {
  return {
    otif_due: d.total_due, otif_passed: d.passed ?? 0, otif_pending: d.pending ?? 0, otif_failed: d.failed ?? 0,
    // The Arrears tab reads its count too (the PO-line payload carries it;
    // the Scheduled payload does not, and then the label stays bare).
    ...(d.arrears != null && { otif_arrears: d.arrears }),
    grn_on_time: d.on_time, grn_early: d.early, grn_late: d.late,
    // Customer review 11 Sep (item 31/32): OTD tile tabs — All = On time +
    // Not on time, the same two numbers as the tile's "met/due" caption.
    ...otdTabCounts(d),
  };
}

/** The OTD tile's three tab counts from a period's delivery block. */
export function otdTabCounts(d: DeliveryMetrics): POLineTabCounts {
  const due = d.otd_due ?? d.total_due;
  return { otd_due: due, otd_met: d.otd_met, otd_missed: Math.max(0, due - d.otd_met) };
}
